import { useState, useEffect, useCallback } from 'react';
import { shoppingService } from '../lib/shopping-service';
import { useFamilyContext } from '../contexts/FamilyContext';

export function useShopping() {
  const { family, currentUser } = useFamilyContext();
  
  // Regular state for items (for loading and real-time updates)
  const [items, setItems] = useState([]);
  
  const [categories, setCategories] = useState([]);
  const [lists, setLists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, error

  // Load initial data
  const loadItems = useCallback(async (filters = {}) => {
    if (!family?.id) return;
    
    console.log('📥 Loading shopping items with filters:', filters);
    
    setLoading(true);
    try {
      const data = await shoppingService.getItems(filters);
      
      const purchasedItems = data.filter(item => item.status === 'purchased');
      const pendingItems = data.filter(item => item.status === 'pending');
      
      console.log('📦 Loaded items:', {
        total: data.length,
        purchased: purchasedItems.length,
        pending: pendingItems.length,
        purchasedItemIds: purchasedItems.map(item => ({ id: item.id, name: item.name }))
      });
      
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading shopping items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [family?.id]);

  const loadCategories = useCallback(async () => {
    if (!family?.id) return;
    
    try {
      const data = await shoppingService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err.message);
    }
  }, [family?.id]);

  const loadLists = useCallback(async (filters = {}) => {
    if (!family?.id) return;
    
    try {
      const data = await shoppingService.getLists(filters);
      setLists(data);
    } catch (err) {
      console.error('Error loading shopping lists:', err);
      setError(err.message);
    }
  }, [family?.id]);

  const loadFavorites = useCallback(async () => {
    if (!family?.id) return;
    
    try {
      const data = await shoppingService.getFavorites();
      setFavorites(data);
    } catch (err) {
      console.error('Error loading favorites:', err);
      setError(err.message);
    }
  }, [family?.id]);

  const loadHistory = useCallback(async (days = 30) => {
    if (!family?.id) return;
    
    try {
      const result = await shoppingService.getHistory({ days });
      // Extract the data array from the result object
      setHistory(result.data || []);
    } catch (err) {
      console.error('Error loading shopping history:', err);
      setError(err.message);
      setHistory([]); // Ensure history is always an array
    }
  }, [family?.id]);

  // Simplified item operations without optimistic updates
  const addItem = useCallback(async (itemData) => {
    if (!currentUser?.id || !family?.id) {
      throw new Error('User not authenticated');
    }
    
    try {
      setSyncStatus('syncing');
      
      // Perform server update directly
      const serverItem = await shoppingService.addItem(itemData, currentUser.id);
      
      // Update local state
      setItems(prev => [serverItem, ...prev]);
      
      setSyncStatus('idle');
      return serverItem;
    } catch (err) {
      console.error('Error adding item:', err);
      setSyncStatus('error');
      setError(err.message);
      throw err;
    }
  }, [currentUser, family?.id]);

  const updateItem = useCallback(async (itemId, updates) => {
    try {
      console.log('✏️ Updating item:', { itemId, updates });
      setSyncStatus('syncing');
      
      // Perform server update
      const updatedItem = await shoppingService.updateItem(itemId, updates);
      
      console.log('✅ Update service call completed, received:', updatedItem);
      
      // Update local state
      setItems(prev => {
        const updated = prev.map(item => 
          item.id === itemId ? updatedItem : item
        );
        
        const modifiedItem = updated.find(item => item.id === itemId);
        console.log('🔄 Local state updated for item:', modifiedItem);
        
        return updated;
      });
      
      setSyncStatus('idle');
      return updatedItem;
    } catch (err) {
      console.error('Error updating item:', err);
      setSyncStatus('error');
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteItem = useCallback(async (itemId) => {
    try {
      setSyncStatus('syncing');
      
      // Perform server deletion
      await shoppingService.deleteItem(itemId);
      
      // Update local state
      setItems(prev => prev.filter(item => item.id !== itemId));
      
      setSyncStatus('idle');
    } catch (err) {
      console.error('Error deleting item:', err);
      setSyncStatus('error');
      setError(err.message);
      throw err;
    }
  }, []);

  const purchaseItem = useCallback(async (itemId, price = null) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    try {
      console.log('📦 Purchasing item:', { itemId, userId: currentUser.id, price });
      
      await shoppingService.purchaseItem(itemId, currentUser.id, price);
      
      console.log('✅ Purchase service call completed, updating local state');
      
      // Update the item in the local state
      setItems(prev => {
        const updated = prev.map(item => 
          item.id === itemId 
            ? { 
                ...item, 
                status: 'purchased', 
                purchased_by: currentUser.id,
                purchased_at: new Date().toISOString(),
                purchase_price: price
              }
            : item
        );
        
        const purchasedItem = updated.find(item => item.id === itemId);
        console.log('🔄 Local state updated for item:', purchasedItem);
        
        return updated;
      });
      
      // Refresh history
      loadHistory();
      setError(null);
    } catch (err) {
      console.error('Error purchasing item:', err);
      setError(err.message);
      throw err;
    }
  }, [currentUser?.id, loadHistory]);

  // List operations
  const createList = useCallback(async (listData) => {
    if (!currentUser?.id || !family?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const newList = await shoppingService.createList(listData, currentUser.id);
      setLists(prev => [newList, ...prev]);
      setError(null);
      return newList;
    } catch (err) {
      console.error('Error creating list:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, family?.id]);

  const updateList = useCallback(async (listId, updates) => {
    setLoading(true);
    try {
      const updatedList = await shoppingService.updateList(listId, updates);
      setLists(prev => prev.map(list => 
        list.id === listId ? updatedList : list
      ));
      setError(null);
      return updatedList;
    } catch (err) {
      console.error('Error updating list:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteList = useCallback(async (listId) => {
    setLoading(true);
    try {
      await shoppingService.deleteList(listId);
      setLists(prev => prev.filter(list => list.id !== listId));
      setError(null);
    } catch (err) {
      console.error('Error deleting list:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Category operations
  const createCategory = useCallback(async (categoryData) => {
    if (!family?.id) {
      throw new Error('Family not found');
    }
    
    setLoading(true);
    try {
      const newCategory = await shoppingService.createCategory(categoryData);
      setCategories(prev => [...prev, newCategory].sort((a, b) => a.sort_order - b.sort_order));
      setError(null);
      return newCategory;
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [family?.id]);

  // Integration functions
  const addItemFromMessage = useCallback(async (messageId, itemData) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const newItem = await shoppingService.addItemFromMessage(messageId, itemData, currentUser.id);
      loadItems(); // Refresh items list
      setError(null);
      return newItem;
    } catch (err) {
      console.error('Error adding item from message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, loadItems]);

  const createListFromEvent = useCallback(async (eventId, listData) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const newList = await shoppingService.createListFromEvent(eventId, listData, currentUser.id);
      setLists(prev => [newList, ...prev]);
      setError(null);
      return newList;
    } catch (err) {
      console.error('Error creating list from event:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  // Utility functions
  const searchItems = useCallback(async (searchTerm) => {
    if (!family?.id) return [];
    
    try {
      const results = await shoppingService.searchItems(searchTerm);
      return results;
    } catch (err) {
      console.error('Error searching items:', err);
      setError(err.message);
      return [];
    }
  }, [family?.id]);

  const sortByStoreLayout = useCallback((itemsToSort, storeKey) => {
    return shoppingService.sortItemsByStoreLayout(itemsToSort, storeKey);
  }, []);

  const categorizeItem = useCallback((itemName) => {
    return shoppingService.categorizeItem(itemName);
  }, []);

  const getStoreLayouts = useCallback(() => {
    return shoppingService.getAllStoreLayouts();
  }, []);

  // Load initial data on mount
  useEffect(() => {
    if (family?.id) {
      loadItems();
      loadCategories();
      loadLists();
      loadFavorites();
      loadHistory();
    }
  }, [family?.id, loadItems, loadCategories, loadLists, loadFavorites, loadHistory]);

  // Real-time subscriptions
  useEffect(() => {
    if (!family?.id) return;

    const itemsSubscription = shoppingService.subscribeToItems((payload) => {
      // Handle real-time updates
      console.log('🔄 Real-time shopping item update received:', payload);
      
      switch (payload.eventType) {
        case 'INSERT':
          setItems(prev => [payload.new, ...prev]);
          break;
        case 'UPDATE':
          setItems(prev => prev.map(item => 
            item.id === payload.new.id ? payload.new : item
          ));
          break;
        case 'DELETE':
          setItems(prev => prev.filter(item => item.id !== payload.old.id));
          break;
        default:
          break;
      }
    });

    const purchasesSubscription = shoppingService.subscribeToPurchases(() => {
      // Refresh history when new purchases are made
      loadHistory();
    });

    return () => {
      shoppingService.unsubscribe(itemsSubscription);
      shoppingService.unsubscribe(purchasesSubscription);
    };
  }, [family?.id, loadHistory]);

  return {
    // Data
    items,
    categories,
    lists,
    favorites,
    history,
    loading,
    error,

    // Optimistic update status
    syncStatus,

    // Item operations (now with optimistic updates!)
    addItem,
    updateItem,
    deleteItem,
    purchaseItem,
    loadItems,

    // List operations
    createList,
    updateList,
    deleteList,
    loadLists,

    // Category operations
    createCategory,
    loadCategories,

    // Integration functions
    addItemFromMessage,
    createListFromEvent,

    // Utility functions
    searchItems,
    sortByStoreLayout,
    categorizeItem,
    getStoreLayouts,

    // Data refresh
    refreshData: () => {
      loadItems();
      loadCategories();
      loadLists();
      loadFavorites();
      loadHistory();
    }
  };
}

// Specialized hook for a single shopping list
export function useShoppingList(listId) {
  const { family } = useFamilyContext();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    if (!family?.id || !listId) return;
    
    setLoading(true);
    try {
      const lists = await shoppingService.getLists({});
      const foundList = lists.find(l => l.id === listId);
      setList(foundList);
      setItems(foundList?.items || []);
      setError(null);
    } catch (err) {
      console.error('Error loading shopping list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [family?.id, listId]);

  const addItemToList = useCallback(async (itemId, addedBy, overrides = {}) => {
    setLoading(true);
    try {
      await shoppingService.addItemToList(listId, itemId, addedBy, overrides);
      loadList(); // Refresh list
      setError(null);
    } catch (err) {
      console.error('Error adding item to list:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [listId, loadList]);

  const removeItemFromList = useCallback(async (itemId) => {
    setLoading(true);
    try {
      await shoppingService.removeItemFromList(listId, itemId);
      setItems(prev => prev.filter(listItem => listItem.item_id !== itemId));
      setError(null);
    } catch (err) {
      console.error('Error removing item from list:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  return {
    list,
    items,
    loading,
    error,
    addItemToList,
    removeItemFromList,
    refreshList: loadList
  };
}